export const PRODUCT_TYPE_ALL = 0;
export const PRODUCT_TYPE_EVO = 1;
export const PRODUCT_TYPE_NOVA = 2;

export const getStoredProductType = () => {
	const parsedProductType = parseInt(localStorage.getItem('productType'));

	if (parsedProductType === PRODUCT_TYPE_EVO || parsedProductType === PRODUCT_TYPE_NOVA) {
		return parsedProductType;
	}

	return PRODUCT_TYPE_ALL;
};

export const getProductTitle = productType => {
	switch (parseInt(productType)) {
		case PRODUCT_TYPE_EVO:
			return 'EVO';
		case PRODUCT_TYPE_NOVA:
			return 'Nova';
		default:
			return 'All Products';
	}
};

export const appendProductFilter = (url, productType) => {
	const parsedProductType = parseInt(productType);

	if (parsedProductType !== PRODUCT_TYPE_EVO && parsedProductType !== PRODUCT_TYPE_NOVA) {
		return url;
	}

	return url + (url.indexOf('?') === -1 ? '?' : '&') + 'ProductID=' + parsedProductType;
};

export const getDeviceProductID = device => {
	if (!device) {
		return null;
	}

	return device.Product || device.product || device.ProductID || device.productID || device.productid || device.ProductId || device.productId;
};

export const getDeviceProductType = device => {
	const productID = getDeviceProductID(device);

	if (productID == PRODUCT_TYPE_EVO) {
		return PRODUCT_TYPE_EVO;
	}

	if (productID == PRODUCT_TYPE_NOVA || (device && device.SerialNumber && String(device.SerialNumber).toUpperCase().startsWith('RJ'))) {
		return PRODUCT_TYPE_NOVA;
	}

	return PRODUCT_TYPE_ALL;
};

export const getDeviceProductPresence = devices => {
	const deviceList = devices || [];
	let hasEvo = false;
	let hasNova = false;

	deviceList.forEach(device => {
		const productType = getDeviceProductType(device);

		if (productType === PRODUCT_TYPE_EVO) {
			hasEvo = true;
		} else if (productType === PRODUCT_TYPE_NOVA) {
			hasNova = true;
		}
	});

	return { hasEvo, hasNova };
};

export const resolveProductSelection = (devices, selectedProductType, canSelectAllProducts) => {
	const parsedProductType = parseInt(selectedProductType);
	const productType = parsedProductType === PRODUCT_TYPE_EVO || parsedProductType === PRODUCT_TYPE_NOVA
		? parsedProductType
		: PRODUCT_TYPE_ALL;

	if (canSelectAllProducts) {
		return { productType, showProductDropDown: true };
	}

	const productPresence = getDeviceProductPresence(devices);
	if (productPresence.hasEvo && productPresence.hasNova) {
		return { productType, showProductDropDown: true };
	}

	if (productPresence.hasEvo) {
		return { productType: PRODUCT_TYPE_EVO, showProductDropDown: false };
	}

	if (productPresence.hasNova) {
		return { productType: PRODUCT_TYPE_NOVA, showProductDropDown: false };
	}

	return { productType: PRODUCT_TYPE_ALL, showProductDropDown: false };
};

export const DASHBOARD_REFRESH_DEBOUNCE_MS = 750;
export const DASHBOARD_RETRY_SLOT_MS = 5000;
export const DASHBOARD_RETRY_MAX_ATTEMPTS = 2;
export const DASHBOARD_POLL_INTERVAL_MS = 600000;

const dashboardRefreshDelays = {
	totalHourlyUsage: 0,
	usedPowerSettings: 5000,
	totalProcedures: 10000,
	laserUsage: 15000,
	wavelengthUsage: 20000,
	topProcedures: 25000,
	topProceduresCat: 30000,
	returnOnInvestment: 35000,
	unitsOn: 40000
};

const fastDashboardRefreshDelays = {
	totalHourlyUsage: 0,
	usedPowerSettings: 500,
	totalProcedures: 1000,
	laserUsage: 1500,
	wavelengthUsage: 2000,
	topProcedures: 2500,
	topProceduresCat: 0,
	returnOnInvestment: 10000,
	unitsOn: 12000
};

export const getDashboardRefreshDelay = (cardName, useFastLoad = true) => {
	const delays = useFastLoad ? fastDashboardRefreshDelays : dashboardRefreshDelays;
	return delays[cardName] || 0;
};

const dashboardQueueTailDelay = Math.max.apply(null, Object.keys(dashboardRefreshDelays).map(cardName => getDashboardRefreshDelay(cardName)));
let nextDashboardRetryAt = 0;

export const getDashboardRefreshWait = cardName => {
	return DASHBOARD_REFRESH_DEBOUNCE_MS + getDashboardRefreshDelay(cardName);
};

export const getDashboardRetryWait = () => {
	const now = Date.now();
	const retryAt = Math.max(now + DASHBOARD_REFRESH_DEBOUNCE_MS + dashboardQueueTailDelay + DASHBOARD_RETRY_SLOT_MS, nextDashboardRetryAt);
	nextDashboardRetryAt = retryAt + DASHBOARD_RETRY_SLOT_MS;

	return retryAt - now;
};

export const beginDashboardRequest = (component, showLoader) => {
	if (component.dashboardAbortController) {
		component.dashboardAbortController.abort();
	}

	const controller = new AbortController();
	component.dashboardAbortController = controller;
	component.dashboardRequestId = (component.dashboardRequestId || 0) + 1;
	component.dashboardRequestSucceeded = false;

	if (showLoader) {
		component.setState({ isLoading: true });
	}

	return {
		controller,
		requestId: component.dashboardRequestId
	};
};

export const isDashboardRequestCurrent = (component, requestId) => {
	return component.dashboardRequestId === requestId;
};

export const stopDashboardPolling = component => {
	if (component.intervalID) {
		clearInterval(component.intervalID);
		component.intervalID = 0;
	}

	if (component.totalProceduresIntervalID) {
		clearInterval(component.totalProceduresIntervalID);
		component.totalProceduresIntervalID = 0;
	}
};

export const markDashboardRequestSucceeded = (component, requestId) => {
	if (isDashboardRequestCurrent(component, requestId)) {
		component.dashboardRequestSucceeded = true;
	}
};

export const startDashboardPolling = (component, pollCallback, interval = DASHBOARD_POLL_INTERVAL_MS) => {
	if (!component.intervalID && typeof pollCallback === 'function') {
		component.intervalID = setInterval(pollCallback, interval);
	}
};

export const finishDashboardRequest = (component, requestId, pollCallback, interval) => {
	if (isDashboardRequestCurrent(component, requestId)) {
		if (!component.dashboardRetryScheduled) {
			component.dashboardRetryCount = 0;
			component.setState({ isLoading: false });
			if (component.dashboardRequestSucceeded) {
				startDashboardPolling(component, pollCallback, interval);
			}
		}
	}
};

export const ignoreDashboardRequestError = error => {
	return error && error.name === 'AbortError';
};

export const scheduleDashboardRetry = (component, requestId, error, retryCallback) => {
	if (ignoreDashboardRequestError(error) || !isDashboardRequestCurrent(component, requestId)) {
		return false;
	}

	component.dashboardRetryCount = component.dashboardRetryCount || 0;

	if (component.dashboardRetryCount >= DASHBOARD_RETRY_MAX_ATTEMPTS) {
		component.dashboardRetryScheduled = false;
		return false;
	}

	component.dashboardRetryCount += 1;
	component.dashboardRetryScheduled = true;

	if (component.dashboardRetryTimeout) {
		clearTimeout(component.dashboardRetryTimeout);
	}

	component.setState({ isLoading: true });

	component.dashboardRetryTimeout = setTimeout(() => {
		component.dashboardRetryScheduled = false;
		retryCallback();
	}, getDashboardRetryWait());

	return true;
};
